import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { PamGrid } from '@1823-partners/core';
import { RowGroupingModule } from 'ag-grid-enterprise';
import { fetchRemittanceRuns } from '../../api/remittance';

const dollars = (value) => (value === null || value === undefined || !Number.isFinite(Number(value))
  ? 'Amount unavailable'
  : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));

const columns = [
  { field: 'loan_id', headerName: 'Loan ID', rowGroup: true, hide: true },
  { field: 'loan_name', headerName: 'Loan name', minWidth: 220 },
  { field: 'transaction_description', headerName: 'Transaction description', minWidth: 240 },
  { field: 'post_date', headerName: 'Post date', valueFormatter: ({ value }) => value?.slice(0, 10) || '' },
  { field: 'effective_date', headerName: 'Effective date', valueFormatter: ({ value }) => value?.slice(0, 10) || '' },
  { field: 'account_description', headerName: 'Account description', minWidth: 220 },
  { field: 'transaction_type', headerName: 'Recon type' },
  { field: 'amount', headerName: 'Excluded amount', pinned: 'right', width: 170, type: 'numericColumn', valueFormatter: ({ value, node }) => node?.group ? '' : dollars(value) },
  { field: 'source_file', headerName: 'Source file', minWidth: 300 },
  { field: 'reason', headerName: 'Exclusion reason', minWidth: 360, wrapText: true, autoHeight: true },
  { field: 'next_step', headerName: 'Next step', minWidth: 360, wrapText: true, autoHeight: true },
  { field: 'cre_id', headerName: 'CRE ID', minWidth: 120 },
];

/** Run-scoped, read-only evidence. Mount with a run key so selection clears the open dialog. */
const ExcludedInputs = ({ run }) => {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const gridRef = useRef(null);
  const available = run.EXCLUSION_DETAILS_AVAILABLE === true;

  useEffect(() => {
    if (!open || !available) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setDetails(null);
    fetchRemittanceRuns({ runId: run.RUN_ID, includeSummary: true })
      .then(({ data }) => {
        if (cancelled) return;
        const matching = Array.isArray(data) ? data.filter((row) => row.RUN_ID === run.RUN_ID) : [];
        if (matching.length !== 1) throw new Error('Run detail unavailable');
        const raw = matching[0].SUMMARY_JSON;
        const summary = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const excluded = summary?.servicer?.excluded_inputs;
        if (excluded?.version !== 1 || !Array.isArray(excluded.rows)
          || excluded.rows.length !== Number(run.UNMATCHED_ROWS)) {
          throw new Error('Incomplete exclusion details');
        }
        setDetails(excluded);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, available, run.RUN_ID, run.UNMATCHED_ROWS, attempt]);

  const reasons = useMemo(() => {
    if (details?.summary_version === 1 && Array.isArray(details.loan_summaries)) {
      return details.loan_summaries.map((row, index) => ({
        ...row, count: row.transaction_count, key: `saved-${index}`,
      }));
    }
    const distinct = new Map();
    (details?.rows || []).forEach((row) => {
      const key = JSON.stringify([row.loan_id, row.loan_name, row.reason, row.next_step]);
      if (!distinct.has(key)) distinct.set(key, { ...row, count: 0, key });
      distinct.get(key).count += 1;
    });
    return [...distinct.values()];
  }, [details]);

  return (
    <Box>
      <Typography component="div" fontWeight={600}>
        {dollars(run.EXCLUDED_NET)} net excluded · {run.UNMATCHED_ROWS} transactions
        {run.EXCLUDED_LOANS != null ? ` · ${run.EXCLUDED_LOANS} loan${Number(run.EXCLUDED_LOANS) === 1 ? '' : 's'}` : ''}
      </Typography>
      <Typography variant="body2">
        These transactions have no crosswalk match and are outside the reconciliation totals.
      </Typography>
      {available ? (
        <Button size="small" onClick={() => setOpen(true)}>View excluded inputs</Button>
      ) : (
        <Typography variant="body2">
          Transaction details unavailable for this older run. Rerun the period to capture them.
        </Typography>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>Excluded inputs — {run.PERIOD}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Run {run.RUN_ID}. These amounts are excluded from reconciliation totals.
            Confirm the Clearwater identifier and complete the loan mapping before rerunning.
          </Typography>
          {reasons.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Why these transactions were excluded</Typography>
              <Typography variant="caption" color="text.secondary">
                Saved with this run. Matches based only on loan names require confirmation.
              </Typography>
              {reasons.map((row) => (
                <Alert key={row.key} severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {row.loan_name || 'Unnamed loan'} · {row.loan_id || 'Loan ID missing'} · {row.count} transaction{row.count === 1 ? '' : 's'}
                  </Typography>
                  <Typography variant="body2">{row.summary_text || row.reason || 'Mapping unresolved.'}</Typography>
                  {!row.summary_text && row.next_step && <Typography variant="body2">Next step: {row.next_step}</Typography>}
                </Alert>
              ))}
            </Box>
          )}
          {error ? (
            <Alert severity="error" action={<Button onClick={() => setAttempt((n) => n + 1)}>Retry</Button>}>
              Could not load this run’s excluded transactions.
            </Alert>
          ) : (
            <PamGrid
              ref={gridRef}
              rowData={details?.rows || []}
              columnDefs={columns}
              getRowId={(data) => data.row_id}
              modules={[RowGroupingModule]}
              groupDefaultExpanded={-1}
              autoGroupColumnDef={{ headerName: 'Loan ID', minWidth: 170, pinned: 'left' }}
              height="420px"
              isLoading={loading}
              showToolbar
              title="Excluded transactions"
              showQuickFilter
              quickFilterPlaceholder="Filter excluded inputs..."
              showExport
              exportConfig={{ fileName: `CMLExcludedInputs_${run.PERIOD}_${run.RUN_ID}.xlsx`, sheetName: 'Excluded inputs' }}
            />
          )}
          {details?.unknown_amount_rows > 0 && (
            <Alert severity="warning">Some transaction amounts are unavailable; the net cannot be calculated.</Alert>
          )}
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ExcludedInputs;
